/**
 * email.helpers
 *
 * Pure, framework-free helpers for the email channel (settings + broadcast).
 * These encapsulate the logic that used to live inline inside
 * `src/pages/sales-channel/email/index.vue` and `src/pages/broadcast/index.vue`
 * so it can be unit tested in isolation and reused consistently.
 *
 * None of these functions touch Vue reactivity, stores, or the network.
 */

export interface EmailSender {
  email: string;
  displayName: string;
}

export interface EmailData {
  subject?: string;
  body?: string;
  editor_type?: string;
  html_editor_mode?: string;
  properties?: {
    importance?: boolean;
    importanceLevel?: string;
    priority?: boolean;
    priorityLevel?: string;
    sensitivity?: boolean;
    sensitivityLevel?: string;
  };
}

export interface EmailMessage {
  message: string[];
  message_type: 'html' | 'text';
  template_type: string;
  email_subject: string;
  email_importance: string;
  email_priority: string;
  email_sensitivity: string;
}

/**
 * Basic email format validation used before adding a sender email.
 * Mirrors the check used on the email settings page: no spaces, an "@" that is
 * not the first character, and a "." after the "@" that is not the last character.
 */
export function isValidEmail(value: string | null | undefined): boolean {
  if (!value || value.includes(' ')) return false;
  const atIndex = value.indexOf('@');
  const dotIndex = value.lastIndexOf('.');
  return atIndex > 0 && dotIndex > atIndex + 1 && dotIndex < value.length - 1;
}

/**
 * Extracts the lowercase domain portion (after the "@") of an email address.
 * Returns '' when there is no domain part.
 */
export function getEmailDomain(email: string | null | undefined): string {
  return String(email || '').split('@')[1]?.toLowerCase() || '';
}

/**
 * Validates that a sender email belongs to the connection's verified domain.
 * When `verifiedDomain` is empty the check is skipped (returns valid) so that
 * connections without a verified domain do not block all additions.
 */
export function validateSenderDomain(
  email: string | null | undefined,
  verifiedDomain: string | null | undefined
): { valid: boolean; verifiedDomain: string } {
  const normalizedVerified = String(verifiedDomain || '').trim().toLowerCase();
  const emailDomain = getEmailDomain(String(email || '').trim());
  if (!normalizedVerified) {
    return { valid: true, verifiedDomain: normalizedVerified };
  }
  return { valid: emailDomain === normalizedVerified, verifiedDomain: normalizedVerified };
}

/**
 * Parses the email connection's `sender_ids` (array of "email:DisplayName"
 * strings, or objects) into a normalized list of { email, displayName }.
 * Falls back to a single `from_email` entry when sender_ids is absent.
 */
export function parseSenderEmails(emailSocialData: any): EmailSender[] {
  if (emailSocialData?.sender_ids?.length) {
    return emailSocialData.sender_ids
      .map((item: any): EmailSender => {
        if (typeof item === 'string') {
          const [email, ...displayParts] = item.split(':');
          return { email: email || '', displayName: displayParts.join(':') || '' };
        }
        return { email: item.email || item, displayName: item.display_name || '' };
      })
      .filter((sender: EmailSender) => sender.email);
  }
  if (emailSocialData?.from_email) {
    return [{ email: emailSocialData.from_email, displayName: emailSocialData.from_display_name || '' }];
  }
  return [];
}

/**
 * Resolves the default sender email to pre-select. `default_sender_id` may be a
 * plain email or in "email:DisplayName" form; we match on the email part and
 * fall back to the first available sender.
 */
export function resolveDefaultSenderEmail(senders: EmailSender[], emailSocialData: any): string {
  if (!senders.length) return '';
  const rawDefault = emailSocialData?.default_sender_id || emailSocialData?.default_sender_email || '';
  const defaultEmail = String(rawDefault).split(':')[0];
  const match = senders.find((s) => s.email === defaultEmail);
  return match ? match.email : senders[0].email;
}

/**
 * Formats the selected sender into the outgoing `email_sender_id` value:
 * "email:DisplayName" when a display name exists, otherwise the plain email.
 * Returns undefined when no sender is selected.
 */
export function formatEmailSenderId(
  selectedEmail: string | null | undefined,
  senders: EmailSender[]
): string | null | undefined {
  // "custom" mirrors the SMS custom sender: send null so the backend resolves the
  // sender per-contact (CRM custom field / account default) instead of a fixed one.
  if (selectedEmail === 'custom') return null;
  if (!selectedEmail) return undefined;
  const sender = senders.find((s) => s.email === selectedEmail);
  if (sender?.displayName) {
    return `${sender.email}:${sender.displayName}`;
  }
  return selectedEmail;
}

/**
 * Derives the outgoing message_type for an email broadcast. The HTML editor
 * (both the Visual and HTML Source sub-tabs) produces "html"; only the plain
 * text editor produces "text".
 */
export function deriveEmailMessageType(editorType: string | null | undefined): 'html' | 'text' {
  return String(editorType || '').toLowerCase() === 'html' ? 'html' : 'text';
}

/**
 * Builds the `messages` array payload entry for an email broadcast from the
 * editor's emailData.
 */
export function buildEmailMessage(emailData: EmailData): EmailMessage {
  const props = emailData.properties || {};
  return {
    message: [emailData.body || ''],
    message_type: deriveEmailMessageType(emailData.editor_type),
    template_type: 'normalchat',
    email_subject: emailData.subject || '',
    email_importance: props.importanceLevel || 'normal',
    email_priority: props.priorityLevel || 'normal',
    email_sensitivity: props.sensitivityLevel || 'none',
  };
}

/**
 * Maps a saved email broadcast (edit/view GET response) into the email_data
 * shape consumed by EmailCreateMessage. Reads the body/message_type from
 * answers[0] and the email meta from the nested answers[0].email object, with a
 * fallback to the top-level email_subject.
 */
export function hydrateEmailDataFromBroadcast(broadcast: any): EmailData {
  const answer = Array.isArray(broadcast?.answers) ? broadcast.answers[0] : null;
  return mapEmailAnswerToEmailData(answer, broadcast?.email_subject);
}

/**
 * Maps the Version B email content of a saved A/B broadcast into the email_data
 * shape. Version B is stored the same way the A/B send writes it: in
 * `answerlisted` (parallel to Version A's `answers`), so we read answerlisted[0].
 * `messagelisted` may be a JSON string (CSV path) or an array (segment path);
 * both are handled as a fallback when answerlisted is absent.
 */
export function hydrateEmailDataFromBroadcastVersionB(broadcast: any): EmailData {
  let answer = Array.isArray(broadcast?.answerlisted) ? broadcast.answerlisted[0] : null;
  if (!answer) {
    let listed = broadcast?.messagelisted;
    if (typeof listed === 'string') {
      try {
        listed = JSON.parse(listed);
      } catch {
        listed = [];
      }
    }
    answer = Array.isArray(listed) ? listed[0] : null;
  }
  return mapEmailAnswerToEmailData(answer, broadcast?.email_subject);
}

/**
 * Shared mapping from a single saved answer/message object into email_data.
 * The email meta (subject/importance/priority/sensitivity) may be nested under
 * an `email` object (GET answers) or flat on the object (sent messages), so we
 * check both. Subject falls back to the provided top-level subject.
 */
function mapEmailAnswerToEmailData(answer: any, fallbackSubject: any): EmailData {
  const body = answer?.message ?? '';
  const isHtml = String(answer?.message_type || '').toLowerCase() === 'html';
  const emailMeta = answer?.email || answer || {};

  const properties: EmailData['properties'] = {};
  if (emailMeta.email_importance) {
    properties.importance = true;
    properties.importanceLevel = emailMeta.email_importance;
  }
  if (emailMeta.email_priority) {
    properties.priority = true;
    properties.priorityLevel = emailMeta.email_priority;
  }
  if (emailMeta.email_sensitivity) {
    properties.sensitivity = true;
    properties.sensitivityLevel = emailMeta.email_sensitivity;
  }

  return {
    subject: emailMeta.email_subject || fallbackSubject || '',
    body,
    editor_type: isHtml ? 'html' : 'plaintext',
    // Saved HTML bodies are raw markup, so open the source sub-tab to show them.
    html_editor_mode: isHtml ? 'source' : 'visual',
    properties,
  };
}
